import React, { useMemo, useState } from "react";
import {
  Avatar,
  Box,
  Chip,
  CircularProgress,
  Pagination,
  Stack,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import GroupOutlinedIcon from "@mui/icons-material/GroupOutlined";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import ConfirmationNumberOutlinedIcon from "@mui/icons-material/ConfirmationNumberOutlined";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@apollo/client/react";
import { GET_EVENT_BY_ID, GET_EVENT_BOOKINGS } from "../graphql/query";
import type { GetEventByIdResponse } from "../interfaces/event.interface";
import type { GetEventBookingsResponse } from "../interfaces/eventBooking.interface";

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatDate(dateStr?: string | null) {
  if (!dateStr) return "—";
  const d = new Date(dateStr.includes("T") ? dateStr : `${dateStr}T00:00:00`);
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

function formatTimeTo12h(timeStr?: string | null) {
  if (!timeStr) return "—";
  const formatSingleTime = (t: string) => {
    const parts = t.split(":");
    if (parts.length < 2) return t;
    const h = parseInt(parts[0], 10);
    const m = parseInt(parts[1], 10);
    if (isNaN(h) || isNaN(m)) return t;
    const ampm = h >= 12 ? "PM" : "AM";
    const h12 = h % 12 || 12;
    return `${h12}:${m.toString().padStart(2, "0")} ${ampm}`;
  };
  if (timeStr.includes("-")) {
    const parts = timeStr.split("-").map((s) => s.trim());
    return `${formatSingleTime(parts[0])} - ${formatSingleTime(parts[1])}`;
  }
  return formatSingleTime(timeStr);
}

function initials(name: string) {
  const parts = (name || "").trim().split(/\s+/).filter(Boolean);
  return ((parts[0]?.[0] ?? "") + (parts[1]?.[0] ?? "")).toUpperCase() || "E";
}

// ─── Shared primitives ────────────────────────────────────────────────────────

function SectionCard({ children, sx = {} }: { children: React.ReactNode; sx?: any }) {
  return (
    <Box
      sx={{
        bgcolor: "#fff",
        border: "1px solid #E9EEF5",
        borderRadius: "16px",
        overflow: "hidden",
        boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}

function SectionHeader({ title, count }: { title: string; count?: number }) {
  return (
    <Box
      sx={{
        px: 3,
        py: 2,
        borderBottom: "1px solid #F0F4F8",
        display: "flex",
        alignItems: "center",
        gap: 1.5,
      }}
    >
      <Typography sx={{ fontWeight: 800, fontSize: 15, color: "#111827" }}>
        {title}
      </Typography>
      {count !== undefined && (
        <Chip
          label={count}
          size="small"
          sx={{ height: 20, fontSize: 11, fontWeight: 700, bgcolor: "#F0F4F8", color: "#6B7280" }}
        />
      )}
    </Box>
  );
}

function InfoItem({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <Box
      sx={{
        display: "flex",
        gap: 1.5,
        alignItems: "flex-start",
        p: 2,
        borderRadius: "12px",
        bgcolor: "#FAFBFC",
        border: "1px solid #F0F4F8",
      }}
    >
      <Box
        sx={{
          width: 34,
          height: 34,
          borderRadius: "10px",
          bgcolor: "#EEF7E9",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#4CAF50",
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography
          variant="caption"
          sx={{ color: "#9CA3AF", fontWeight: 700, letterSpacing: "0.5px", textTransform: "uppercase", fontSize: 10 }}
        >
          {label}
        </Typography>
        <Typography sx={{ fontWeight: 600, color: "#111827", fontSize: 13, mt: 0.25, wordBreak: "break-word" }}>
          {value}
        </Typography>
      </Box>
    </Box>
  );
}

// ─── Overview Section ─────────────────────────────────────────────────────────

function OverviewSection({ event }: { event: any }) {
  const detailItems = [
    { icon: <LocationOnOutlinedIcon sx={{ fontSize: 18 }} />, label: "Location", value: event.location || "—" },
    { icon: <CalendarMonthOutlinedIcon sx={{ fontSize: 18 }} />, label: "Start Date", value: formatDate(event.startDate) },
    { icon: <CalendarMonthOutlinedIcon sx={{ fontSize: 18 }} />, label: "End Date", value: formatDate(event.endDate) },
    { icon: <AccessTimeIcon sx={{ fontSize: 18 }} />, label: "Time", value: formatTimeTo12h(event.time) },
    { icon: <AccessTimeIcon sx={{ fontSize: 18 }} />, label: "Duration", value: event.duration || "—" },
    { icon: <Typography sx={{ fontSize: 18, fontWeight: 900 }}>₹</Typography>, label: "Standard Price", value: event.standardPrice ? `₹${event.standardPrice}` : "—" },
  ];

  const instructors: string[] = event.instructorNames || event.instructors || [];
  const offers: any[] = event.offers || [];

  return (
    <Stack spacing={2}>
      <SectionCard>
        <SectionHeader title="Event Details" />
        <Box sx={{ p: 2.5, display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 1.5 }}>
          {detailItems.map((item) => (
            <InfoItem key={item.label} icon={item.icon} label={item.label} value={item.value} />
          ))}
        </Box>

        {instructors.length > 0 && (
          <Box sx={{ px: 2.5, pb: 2.5 }}>
            <Box sx={{ p: 2, borderRadius: "12px", bgcolor: "#FAFBFC", border: "1px solid #F0F4F8", display: "flex", gap: 1.5, alignItems: "flex-start" }}>
              <Box sx={{ width: 34, height: 34, borderRadius: "10px", bgcolor: "#EEF7E9", display: "flex", alignItems: "center", justifyContent: "center", color: "#4CAF50", flexShrink: 0 }}>
                <PersonOutlineIcon sx={{ fontSize: 18 }} />
              </Box>
              <Box>
                <Typography variant="caption" sx={{ color: "#9CA3AF", fontWeight: 700, letterSpacing: "0.5px", textTransform: "uppercase", fontSize: 10, display: "block" }}>
                  Instructors
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mt: 0.75 }}>
                  {instructors.map((inst: string) => (
                    <Chip
                      key={inst}
                      label={inst}
                      size="small"
                      sx={{ height: 24, fontSize: 12, fontWeight: 700, bgcolor: "#EEF7E9", color: "#2E7D32", border: "1px solid #C8E6C9" }}
                    />
                  ))}
                </Box>
              </Box>
            </Box>
          </Box>
        )}

        {event.description && (
          <Box sx={{ px: 2.5, pb: 2.5 }}>
            <Box sx={{ p: 2, borderRadius: "12px", bgcolor: "#FAFBFC", border: "1px solid #F0F4F8", display: "flex", gap: 1.5, alignItems: "flex-start" }}>
              <Box sx={{ width: 34, height: 34, borderRadius: "10px", bgcolor: "#EEF7E9", display: "flex", alignItems: "center", justifyContent: "center", color: "#4CAF50", flexShrink: 0 }}>
                <DescriptionOutlinedIcon sx={{ fontSize: 18 }} />
              </Box>
              <Box>
                <Typography variant="caption" sx={{ color: "#9CA3AF", fontWeight: 700, letterSpacing: "0.5px", textTransform: "uppercase", fontSize: 10 }}>
                  Description
                </Typography>
                <Typography sx={{ fontSize: 13, color: "#374151", fontWeight: 500, mt: 0.5, lineHeight: 1.7 }}>
                  {event.description}
                </Typography>
              </Box>
            </Box>
          </Box>
        )}
      </SectionCard>

      {offers.length > 0 && (
        <SectionCard>
          <SectionHeader title="Offers / Promo Codes" count={offers.length} />
          <Box sx={{ p: 2.5, display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 1.5 }}>
            {offers.map((offer: any, idx: number) => (
              <Box
                key={idx}
                sx={{
                  border: "1px solid #E9EEF5",
                  borderRadius: "14px",
                  overflow: "hidden",
                  bgcolor: "#FAFBFC",
                }}
              >
                <Box sx={{ height: 4, bgcolor: "#4CAF50", borderRadius: "14px 14px 0 0" }} />
                <Box sx={{ p: 2 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <LocalOfferOutlinedIcon sx={{ fontSize: 16, color: "#4CAF50" }} />
                      <Typography sx={{ fontWeight: 800, fontSize: 14, color: "#111827" }}>
                        {offer.promoCode || "—"}
                      </Typography>
                    </Box>
                    {offer.offerPrice && (
                      <Chip
                        label={`₹${offer.offerPrice}`}
                        size="small"
                        sx={{ height: 22, fontWeight: 800, fontSize: 11, bgcolor: "#E8F5E9", color: "#2E7D32" }}
                      />
                    )}
                  </Stack>
                  <Stack direction="row" spacing={2}>
                    <Box>
                      <Typography variant="caption" sx={{ color: "#9CA3AF", fontWeight: 700, textTransform: "uppercase", fontSize: 10 }}>From</Typography>
                      <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#374151" }}>{formatDate(offer.offerStartDate)}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" sx={{ color: "#9CA3AF", fontWeight: 700, textTransform: "uppercase", fontSize: 10 }}>To</Typography>
                      <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#374151" }}>{formatDate(offer.offerEndDate)}</Typography>
                    </Box>
                  </Stack>
                </Box>
              </Box>
            ))}
          </Box>
        </SectionCard>
      )}

      {event.gallery && event.gallery.length > 0 && (
        <SectionCard>
          <SectionHeader title="Gallery" count={event.gallery.length} />
          <Box
            sx={{
              p: 2.5,
              display: "grid",
              gridTemplateColumns: { xs: "repeat(2, 1fr)", sm: "repeat(3, 1fr)", md: "repeat(4, 1fr)" },
              gap: 1.5,
            }}
          >
            {event.gallery.map((img: string, idx: number) => (
              <Box
                key={idx}
                sx={{ aspectRatio: "1", borderRadius: "12px", overflow: "hidden", border: "1px solid #E9EEF5", bgcolor: "#F9FAFB" }}
              >
                <Box
                  component="img"
                  src={`data:image/jpeg;base64,${img}`}
                  alt={`Gallery ${idx + 1}`}
                  sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
              </Box>
            ))}
          </Box>
        </SectionCard>
      )}
    </Stack>
  );
}

// ─── Attendees Section ────────────────────────────────────────────────────────

const ATTENDEES_PAGE_SIZE = 8;

function AttendeesSection({ bookings }: { bookings: any[] }) {
  const [page, setPage] = useState(1);
  const pageCount = Math.ceil(bookings.length / ATTENDEES_PAGE_SIZE);
  const paged = bookings.slice((page - 1) * ATTENDEES_PAGE_SIZE, page * ATTENDEES_PAGE_SIZE);

  if (bookings.length === 0) {
    return (
      <SectionCard>
        <Box sx={{ py: 8, textAlign: "center" }}>
          <GroupOutlinedIcon sx={{ fontSize: 48, color: "#D1D5DB", mb: 1.5 }} />
          <Typography sx={{ color: "#9CA3AF", fontWeight: 600 }}>No attendees booked yet</Typography>
        </Box>
      </SectionCard>
    );
  }

  return (
    <SectionCard>
      <SectionHeader title="Registered Attendees" count={bookings.length} />
      <Box sx={{ p: 2.5, display: "flex", flexDirection: "column", gap: 1.5 }}>
        {paged.map((booking: any, idx: number) => (
          <Box
            key={booking.id || idx}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              p: 2,
              borderRadius: "12px",
              border: "1px solid #F0F4F8",
              bgcolor: "#FAFBFC",
            }}
          >
            <Avatar
              sx={{
                width: 38,
                height: 38,
                bgcolor: "#a3d65c",
                color: "#fff",
                fontWeight: 900,
                fontSize: 14,
                flexShrink: 0,
              }}
            >
              {initials(booking.studentName || "")}
            </Avatar>
            <Box flex={1} sx={{ minWidth: 0 }}>
              <Typography sx={{ fontWeight: 700, fontSize: 14, color: "#111827" }} noWrap>
                {booking.studentName || "—"}
              </Typography>
              <Typography variant="caption" sx={{ color: "#9CA3AF", fontWeight: 600 }}>
                {booking.studentEmail || "—"} · Booked {formatDate(booking.bookedAt)}
              </Typography>
            </Box>
            <Chip
              label={booking.status === "CONFIRMED" ? "Confirmed" : booking.status || "—"}
              size="small"
              icon={
                booking.status === "CONFIRMED"
                  ? <CheckCircleOutlineIcon sx={{ fontSize: "14px !important" }} />
                  : <CancelOutlinedIcon sx={{ fontSize: "14px !important" }} />
              }
              sx={{
                height: 24,
                fontWeight: 700,
                fontSize: 11,
                flexShrink: 0,
                bgcolor: booking.status === "CONFIRMED" ? "#E8F5E9" : "#FEF2F2",
                color: booking.status === "CONFIRMED" ? "#2E7D32" : "#B91C1C",
              }}
            />
          </Box>
        ))}
      </Box>
      {pageCount > 1 && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 2, borderTop: "1px solid #F0F4F8" }}>
          <Pagination
            count={pageCount}
            page={page}
            onChange={(_, v) => setPage(v)}
            size="small"
            sx={{ "& .MuiPaginationItem-root": { fontWeight: 700 }, "& .Mui-selected": { bgcolor: "#a3d65c !important", color: "#fff" } }}
          />
        </Box>
      )}
    </SectionCard>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

const TABS = [
  { label: "Overview", icon: <ConfirmationNumberOutlinedIcon sx={{ fontSize: 16 }} /> },
  { label: "Attendees", icon: <GroupOutlinedIcon sx={{ fontSize: 16 }} /> },
];

const EventReport: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);

  const { data: eventData, loading: eventLoading } = useQuery<GetEventByIdResponse>(
    GET_EVENT_BY_ID,
    { variables: { id }, skip: !id, fetchPolicy: "network-only" }
  );

  const { data: bookingsData, loading: bookingsLoading } = useQuery<GetEventBookingsResponse>(
    GET_EVENT_BOOKINGS,
    { variables: { eventId: id }, skip: !id, fetchPolicy: "network-only" }
  );

  const isLoading = eventLoading || bookingsLoading;
  const event = eventData?.getEventById?.data;
  const bookings = bookingsData?.getEventBookings?.data ?? [];

  const quickStats = useMemo(() => ({
    attendees: bookings.length,
    confirmed: bookings.filter((b: any) => b.status === "CONFIRMED").length,
    offers: (event?.offers || []).length,
  }), [bookings, event]);

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "60vh" }}>
        <CircularProgress size={36} sx={{ color: "#a3d65c" }} />
      </Box>
    );
  }

  if (!event) {
    return (
      <Box sx={{ mt: 8, textAlign: "center" }}>
        <Typography sx={{ color: "#9CA3AF", fontWeight: 600 }}>Event not found.</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ mt: { xs: 1, sm: 2 }, px: { xs: 1, md: 3 }, pb: 6 }}>
      {/* Back link */}
      <Box
        onClick={() => navigate("/events")}
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 0.5,
          cursor: "pointer",
          color: "#6B7280",
          fontSize: 13,
          fontWeight: 600,
          mb: 2,
          "&:hover": { color: "#111827" },
        }}
      >
        <ArrowBackIosNewIcon sx={{ fontSize: 12 }} />
        Back to Events
      </Box>

      {/* ── Hero Card ── */}
      <Box
        sx={{
          bgcolor: "#fff",
          border: "1px solid #E9EEF5",
          borderRadius: "20px",
          overflow: "hidden",
          mb: 2.5,
          boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
        }}
      >
        {event.thumbnail && (
          <Box
            component="img"
            src={`data:image/jpeg;base64,${event.thumbnail}`}
            alt={event.eventName}
            sx={{ width: "100%", height: { xs: 160, sm: 220 }, objectFit: "cover" }}
          />
        )}
        <Box sx={{ p: 3 }}>
          <Stack direction={{ xs: "column", sm: "row" }} gap={2.5} alignItems={{ sm: "flex-start" }}>
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: "16px",
                bgcolor: "#EEF7E9",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                border: "2px solid #C8E6C9",
              }}
            >
              <ConfirmationNumberOutlinedIcon sx={{ fontSize: 30, color: "#4CAF50" }} />
            </Box>

            <Box flex={1}>
              <Stack direction="row" alignItems="center" gap={1.5} flexWrap="wrap">
                <Typography sx={{ fontWeight: 900, fontSize: 22, color: "#111827", lineHeight: 1.2 }}>
                  {event.eventName}
                </Typography>
                {(event as any).isOfferActive && (
                  <Chip
                    label="Offer Active"
                    size="small"
                    sx={{ height: 24, fontWeight: 800, fontSize: 11, bgcolor: "#FFF8E1", color: "#F57F17" }}
                  />
                )}
              </Stack>
              <Typography sx={{ color: "#9CA3AF", fontWeight: 600, fontSize: 13, mt: 0.5 }}>
                {formatDate(event.startDate)} – {formatDate(event.endDate)} · {event.location}
              </Typography>
            </Box>
          </Stack>

          {/* Quick stats strip */}
          <Box sx={{ mt: 3, display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 1.5 }}>
            {[
              { label: "Total Attendees", value: quickStats.attendees, color: "#1565C0", bg: "#EBF2FF" },
              { label: "Confirmed", value: quickStats.confirmed, color: "#4CAF50", bg: "#E8F5E9" },
              { label: "Active Offers", value: quickStats.offers, color: "#7C3AED", bg: "#F5F0FF" },
            ].map((s) => (
              <Box
                key={s.label}
                sx={{
                  textAlign: "center",
                  py: 1.75,
                  px: 1,
                  borderRadius: "12px",
                  bgcolor: s.bg,
                  border: `1px solid ${s.color}22`,
                }}
              >
                <Typography sx={{ fontWeight: 900, fontSize: 24, color: s.color, lineHeight: 1 }}>
                  {s.value}
                </Typography>
                <Typography variant="caption" sx={{ fontWeight: 700, color: "#6B7280", fontSize: 11 }}>
                  {s.label}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      {/* ── Tabs ── */}
      <Box
        sx={{
          bgcolor: "#fff",
          border: "1px solid #E9EEF5",
          borderRadius: "14px",
          mb: 2.5,
          boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
          overflow: "hidden",
        }}
      >
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          variant="fullWidth"
          sx={{
            minHeight: 48,
            "& .MuiTab-root": { textTransform: "none", fontWeight: 700, fontSize: 13.5, color: "#9CA3AF", minHeight: 48, gap: 0.75 },
            "& .Mui-selected": { color: "#111827 !important", fontWeight: 800 },
            "& .MuiTabs-indicator": { backgroundColor: "#a3d65c", height: 3, borderRadius: "3px 3px 0 0" },
          }}
        >
          {TABS.map((t, i) => {
            const counts = [undefined, bookings.length];
            const count = counts[i];
            return (
              <Tab key={t.label} label={count !== undefined ? `${t.label} (${count})` : t.label} icon={t.icon} iconPosition="start" />
            );
          })}
        </Tabs>
      </Box>

      {/* ── Tab content ── */}
      {activeTab === 0 && <OverviewSection event={event} />}
      {activeTab === 1 && <AttendeesSection bookings={bookings} />}
    </Box>
  );
};
export default EventReport;
